import React, { useState } from 'react';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Button,
  ButtonSizes,
  ButtonTypes,
} from 'app/common/components/controls/button/button';
import {
  useTeamfulCollaborationAddBoardsQuery,
  useTeamfulCollaborationMoveBoardToOrgMutation,
} from '@trello/graphql/generated';
import { Checkbox } from '@trello/nachos';
import { Spinner } from 'app/src/components/Spinner';
import classNames from 'classnames';
import styles from './TeamfulCollaborationAddBoards.less';
import { Noun, Verb, trackUe } from '@trello/analytics';

const Alert = require('app/scripts/views/lib/alerts');

const format = forNamespace('teamful collaboration prompt');
const noop = () => {};

interface TeamfulCollaborationAddBoardsProps {
  onContinue: () => void;
  orgId: string;
}

export const TeamfulCollaborationAddBoards: React.FunctionComponent<TeamfulCollaborationAddBoardsProps> = ({
  onContinue,
  orgId,
}) => {
  const [
    moveBoardToOrgMutation,
  ] = useTeamfulCollaborationMoveBoardToOrgMutation();
  const initialState: Set<string> = new Set();
  const boardEntries = [];
  const maxBoardsShown = 4;
  const [selectedBoards, setSelected] = useState(initialState);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { data, loading } = useTeamfulCollaborationAddBoardsQuery({
    fetchPolicy: 'network-only',
  });
  const getOpenBoards = () => {
    if (loading) {
      return [];
    }

    const member = data?.member;
    if (!member) {
      return [];
    }

    const myId = member.id;
    const openBoards = [];

    for (let i = 0; i < member.boards.length; i++) {
      const board = member.boards[i];
      if (board.enterpriseOwned || board.idOrganization !== null) {
        continue;
      }

      const memberships = board.memberships;
      let memberCount = 0;
      let amIAdmin = false;

      for (let j = 0; j < memberships.length; j++) {
        const { idMember, memberType, unconfirmed } = memberships[j];

        if (!amIAdmin && idMember === myId && memberType === 'admin') {
          amIAdmin = true;
        }

        if (!unconfirmed) {
          memberCount += 1;
        }
      }

      if (memberCount >= 3 && amIAdmin) {
        openBoards.push(board);
      }
    }

    return openBoards;
  };

  const openBoards = getOpenBoards();

  const entryLimit = Math.min(openBoards.length, maxBoardsShown);
  for (let i = 0; i < entryLimit; i++) {
    const board = openBoards[i];
    const backgroundColor = board?.prefs?.backgroundColor
      ? board.prefs.backgroundColor
      : undefined;
    const backgroundImage = board?.prefs?.backgroundImage
      ? board.prefs.backgroundImage
      : undefined;

    const entry = (
      <BoardListItem
        onSelect={() => {
          if (selectedBoards.has(board.id)) {
            selectedBoards.delete(board.id);
          } else {
            selectedBoards.add(board.id);
          }

          setSelected(new Set(selectedBoards));
        }}
        selected={selectedBoards.has(board.id)}
        name={board.name}
        backgroundImage={backgroundImage}
        backgroundColor={backgroundColor}
        key={board.id}
      />
    );
    boardEntries.push(entry);
  }

  const onSubmit = async () => {
    setIsSubmitted(true);
    const selectedBoardsArray: Array<string> = Array.from(selectedBoards);

    trackUe({
      category: Noun.TEAM_PAGE,
      verb: Verb.SUBMITS,
      directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
      context: {
        orgId,
        addedBoardsCount: selectedBoardsArray.length,
        availableBoardsCount: openBoards.length,
      },
    });

    try {
      const mutations = [];

      for (let i = 0; i < selectedBoardsArray.length; i++) {
        const resp = moveBoardToOrgMutation({
          variables: {
            boardId: selectedBoardsArray[i],
            orgId,
          },
        });
        mutations.push(resp);
      }

      await Promise.all(mutations);
      onContinue();
    } catch (e) {
      Alert.showLiteralText(e.message, 'error', 'alert', 5000);
      setIsSubmitted(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.addBoardsLoadingState}>
        <Spinner centered />
        <div>{format(['loading'])}</div>
      </div>
    );
  } else if (boardEntries.length === 0) {
    trackUe({
      category: Noun.TEAM_PAGE,
      verb: Verb.SUBMITS,
      directObj: Noun.TEAMLESS_COLLABORATION_PROMPT,
      context: {
        orgId,
        addedBoardsCount: 0,
        availableBoardsCount: 0,
      },
    });
    onContinue();
    return null;
  }

  return (
    <div className={styles.addBoards}>
      <div className={styles.addBoardsTitle}>
        {format(['add boards form', 'title'])}
      </div>
      <div className={styles.addBoardsDescription}>
        {format(['add boards form', 'description'])}
      </div>
      {boardEntries}
      <div className={styles.addBoardsSubmitMessaging}>
        {format(['add boards form', 'submit messaging'])}
      </div>
      <Button
        buttonType={isSubmitted ? ButtonTypes.DISABLED : ButtonTypes.PRIMARY}
        className={styles.addBoardsButton}
        onClick={isSubmitted ? noop : onSubmit}
        size={ButtonSizes.FULLWIDTH}
      >
        {isSubmitted ? <Spinner centered /> : format(['next'])}
      </Button>
    </div>
  );
};

interface BoardLineEntryProps {
  name: string;
  selected: boolean;
  onSelect: () => void;
  backgroundImage: string | undefined;
  backgroundColor: string | undefined;
}

export const BoardListItem: React.FunctionComponent<BoardLineEntryProps> = ({
  name,
  selected,
  onSelect,
  backgroundImage,
  backgroundColor,
}) => {
  const tileStyle = {
    backgroundColor: backgroundColor,
  };

  const [isHovered, setHoverState] = useState(false);

  return (
    <div
      role="checkbox"
      aria-checked={selected}
      onClick={onSelect}
      className={classNames(styles.boardListEntry, {
        [styles.boardListEntryHover]: isHovered,
      })}
    >
      <div className={styles.backgroundTile} style={tileStyle}>
        {backgroundImage && (
          <img alt="board-background" src={backgroundImage} />
        )}
      </div>
      <p>{name}</p>
      <div
        onMouseEnter={() => setHoverState(true)}
        onMouseLeave={() => setHoverState(false)}
        className={styles.checkbox}
      >
        <Checkbox isFullWidth isChecked={selected} />
      </div>
    </div>
  );
};
